import logger from './logger.js';
import { loadWebSdk } from './experimentWeb.js';
export function createMtmConnector(config) {
    if (!config || !/^[a-f0-9]{32}$/i.test(config.apiKey)
        || typeof config.getUser !== 'function' || typeof config.getProjectApiKey !== 'function') {
        const error = new Error('MTM Experiment connector requires the project API key and verified destination identity/project getters');
        logger.error(error);
        throw error;
    }
    let identity;
    function isConsentGranted() {
        const analytics = window.analytics;
        if (typeof analytics?.get !== 'function')
            return false;
        const permission = analytics.get('Amplitude');
        return permission === true || permission === 'true';
    }
    return {
        isConsentGranted,
        getProjectApiKey: () => config.getProjectApiKey(),
        getUser() {
            const analytics = window.analytics;
            if (!isConsentGranted() || typeof analytics?.isReady !== 'function' || analytics.isReady() !== true)
                return undefined;
            const project = config.getProjectApiKey();
            if (project === undefined)
                return undefined;
            if (project !== config.apiKey) {
                const error = new Error('Experiment project does not match the verified Amplitude event destination; no events may be sent');
                logger.error(error);
                throw error;
            }
            // Use the destination's identity, never the account session ID.
            const user = config.getUser();
            if (identity && user && (user.device_id !== identity.device_id || user.user_id !== identity.user_id)) {
                const error = new Error('Experiment destination identity changed; reload before measuring a new identity');
                logger.error(error);
                throw error;
            }
            if (user && !identity)
                identity = { device_id: user.device_id, user_id: user.user_id };
            return user;
        },
    };
}
let active;
export function initializeExperiment(config) {
    const invalid = !config || typeof config.getUser !== 'function' || typeof config.isConsentGranted !== 'function';
    const timeoutMs = config?.timeoutMs ?? 30000;
    if (invalid || !Number.isFinite(timeoutMs) || timeoutMs <= 0 || timeoutMs > 2147483647
        || (config.onStateChange !== undefined && typeof config.onStateChange !== 'function')) {
        const error = new Error('Experiment requires connector getUser, Amplitude isConsentGranted and a positive timeoutMs');
        logger.error(error);
        throw error;
    }
    if (active || window.experimentIntegration) {
        const error = new Error('Experiment integration is already installed; do not initialize a second event path');
        logger.error(error);
        throw error;
    }
    let state = { status: 'waiting', message: 'Waiting for Amplitude consent, connector identity and MTM readiness' };
    let identity;
    let pending;
    let timer;
    let rejectPending;
    function update(status, message) {
        if (state.status === 'disposed')
            return;
        if (state.status === status && state.message === message)
            return;
        state = { status, message };
        if (status === 'error')
            logger.error(`Experiment: ${message}`);
        if (status === 'blocked')
            logger.warn(`Experiment: ${message}`);
        if (config.onStateChange) {
            try {
                config.onStateChange({ ...state });
            }
            catch (error) {
                logger.error(error);
            }
        }
    }
    function currentUser() {
        if (state.status === 'disposed')
            return undefined;
        // This hook reads the existing Amplitude service, not a broad consent category.
        if (config.isConsentGranted() !== true) {
            update('blocked', 'Amplitude consent is not granted; no experiment events are handed off');
            return undefined;
        }
        const analytics = window.analytics;
        if (!analytics || typeof analytics.isReady !== 'function' || typeof analytics.trackAndVerify !== 'function'
            || analytics.isReady() !== true) {
            update('waiting', 'Waiting for the MTM/Segment event path');
            return undefined;
        }
        const user = config.getUser();
        if (!user) {
            update('waiting', 'Waiting for connector identity');
            return undefined;
        }
        const validId = (id) => typeof id === 'string' && id.trim().length > 0;
        if ((!validId(user.device_id) && !validId(user.user_id))
            || (user.device_id !== undefined && !validId(user.device_id))
            || (user.user_id !== undefined && !validId(user.user_id))) {
            throw new Error('Connector must provide a non-empty Amplitude device_id or user_id');
        }
        return { ...user };
    }
    function fail(error) {
        const reason = error instanceof Error ? error : new Error(String(error));
        update('error', reason.message);
        return reason;
    }
    const integration = {
        setup() {
            if (state.status === 'disposed')
                return Promise.reject(new Error('Experiment integration is disposed'));
            if (pending)
                return pending;
            const deadline = Date.now() + timeoutMs;
            pending = new Promise((resolve, reject) => {
                rejectPending = reject;
                const check = () => {
                    try {
                        const user = currentUser();
                        if (state.status === 'disposed')
                            return;
                        if (user) {
                            identity = user;
                            update('ready', 'Connector identity and consent ready; MTM handoff is not ingestion proof');
                            resolve();
                            return;
                        }
                        if (Date.now() >= deadline)
                            throw new Error('Timed out waiting for Amplitude consent, connector identity and MTM readiness');
                        timer = setTimeout(check, 100);
                    }
                    catch (error) {
                        reject(fail(error));
                    }
                };
                check();
            });
            const clearPending = () => {
                pending = undefined;
                rejectPending = undefined;
                timer = undefined;
            };
            pending.then(clearPending, clearPending);
            return pending;
        },
        getUser() {
            try {
                const user = currentUser();
                if (!user)
                    throw new Error('Experiment identity is unavailable without consent and MTM readiness');
                identity = user;
                update('ready', 'Connector identity and consent ready; MTM handoff is not ingestion proof');
                if (state.status === 'disposed')
                    throw new Error('Experiment integration is disposed');
                return { ...user };
            }
            catch (error) {
                throw fail(error);
            }
        },
        track(event) {
            try {
                const user = currentUser();
                if (!user)
                    return false;
                if (!identity || user.device_id !== identity.device_id || user.user_id !== identity.user_id) {
                    update('blocked', 'Connector identity changed; refresh Experiment identity before measuring');
                    return false;
                }
                if (!event || typeof event.eventType !== 'string' || !event.eventType.trim()
                    || (event.eventProperties !== undefined && (!event.eventProperties || typeof event.eventProperties !== 'object' || Array.isArray(event.eventProperties)))) {
                    throw new Error('Experiment supplied an invalid event or eventProperties');
                }
                const analytics = window.analytics;
                if (!analytics || typeof analytics.isReady !== 'function' || typeof analytics.trackAndVerify !== 'function'
                    || analytics.isReady() !== true)
                    throw new Error('MTM event path is not ready');
                if (analytics.trackAndVerify(event.eventType, event.eventProperties) !== true) {
                    throw new Error('Experiment event was not handed to MTM');
                }
                // Acknowledges only the existing MTM handoff, not backend ingestion.
                update('ready', 'Event handed to MTM; verify ingestion and attribution independently');
                return true;
            }
            catch (error) {
                fail(error);
                return false;
            }
        },
        getState() {
            if (state.status === 'ready') {
                try {
                    const user = currentUser();
                    if (user && identity && (user.device_id !== identity.device_id || user.user_id !== identity.user_id)) {
                        update('blocked', 'Connector identity changed; refresh Experiment identity before measuring');
                    }
                }
                catch (error) {
                    fail(error);
                }
            }
            return { ...state };
        },
        dispose() {
            if (timer !== undefined)
                clearTimeout(timer);
            rejectPending?.(new Error('Experiment integration disposed while waiting'));
            identity = undefined;
            update('disposed', 'Experiment adapter disposed; no further events are handed off');
            if (window.experimentIntegration === integration)
                delete window.experimentIntegration;
            if (active === integration)
                active = undefined;
        },
    };
    active = integration;
    window.experimentIntegration = integration;
    return integration;
}
export function getIntegration() {
    return active;
}
export function dispose() {
    active?.dispose();
}
export default { initialize: initializeExperiment, getIntegration, createMtmConnector, loadWebSdk, dispose };
